import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Box,
  Text,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";

//hooks
import useHashConnect from "hooks/useHashConnect";

//utils
import { nativeTokenFormatter } from "utils";
import {
  FIREBASE_EVENT_PROPERTIES,
  NATIVE_TOKEN_LABEL,
  precision,
} from "constants/constants";

// types
import { TransactionCResponse } from "types/TransactionCResponse";

// components
import TimerModal from "components/TimerModal";
import ModalWrapper from "ui/ModalWrapper/ModalWrapper";

// images
import TickIcon from "assets/tick.svg";

// third party
import moment from "moment";
import { debounce } from "lodash";
import Image from "next/image";

// icons
import hederaDark from "assets/hederaDark.svg";
import hederaLight from "assets/hederaLight.svg";

// config
import { config } from "config/config";
import { Alert } from "ui/Alert";
import { WalletActionBtn } from "components/WalletActionBtn/WalletActionBtn";

// firebase analytics
import { firebaseEvents } from "utils/firebase";
interface Transaction {
  amount: number;
  exchangeRate: number;
  index: number;
  isWithDrawn: boolean;
  time: number;
  contractType?: string | null;
}

interface PropTypes {
  transaction: Transaction;
  setTransactionResult?: (r: string) => void;
  setTransactionResultId?: (r: string) => void;
  showTransactionResultModal?: (r: boolean) => void;
  transactionFee?: number | undefined;
  selectedAccount?: string;
}

const ClaimItem = ({
  transaction,
  setTransactionResult,
  setTransactionResultId,
  showTransactionResultModal,
  transactionFee,
  selectedAccount,
}: PropTypes) => {
  const {
    amount,
    index,
    isWithDrawn,
    time,
    exchangeRate,
    contractType: transactionContractType,
  } = transaction;
  // const [err, setErr] = useState("");
  const { withdraw, withdrawStatus, withdrawId, walletData, contractType } =
    useHashConnect();

  const [underMaintainanceModal, showUnderMaintainanceModal] =
    useState<boolean>(false);

  const [timestamp, setTimestamp] = useState<number>(
    Math.floor(new Date().getTime() / 1000)
  );

  const [confirmWithdrawModal, setConfirmWithdrawModal] =
    useState<boolean>(false);
  const hederaIcon = useColorModeValue(hederaLight, hederaDark);
  const sendLog = (name: string) => {
    let firebaseRequest: any = {
      [FIREBASE_EVENT_PROPERTIES.EVENT_NAME]: name,
      [FIREBASE_EVENT_PROPERTIES.WALLET_NAME]:
        walletData.pairedWalletData?.name,
      [FIREBASE_EVENT_PROPERTIES.WALLET_ADDRESS]: selectedAccount,
      [FIREBASE_EVENT_PROPERTIES.WITHDRAW_AMOUNT]: nativeTokenFormatter(
        Number(amount)
      ),
    };
    firebaseEvents(firebaseRequest);
  };
  const getTimeStamp = async () => {
    const timestamp = Math.floor(new Date().getTime() / 1000);
    try {
      //TODO:  url does not work for vercel temp deployment so added full link. will remove this later
      const resp: any = await fetch(
        `https://hedera.staderlabs.com/api/timestamp`
      );
      const response: any = await resp.json();
      if (response?.timestamp) {
        setTimestamp(response.data.timestamp);
      }
    } catch {
      setTimestamp(timestamp);
    }
  };

  useEffect(() => {
    getTimeStamp();
  }, []);

  const handleClaim = async () => {
    sendLog("Withdraw_cta");
    const response: TransactionCResponse = await withdraw(
      index,
      transactionContractType ? transactionContractType : null
    );
    if (response?.status === "UNDER_MAINTAINANCE") {
      showUnderMaintainanceModal(true);
      return;
    }
    setTransactionResult && setTransactionResult(response?.status);
    setTransactionResultId && setTransactionResultId(response?.transactionId);
    showTransactionResultModal && showTransactionResultModal(true);
    sendLog(
      response?.status === "SUCCESS"
        ? "withdrawal_successful"
        : "waithdrawal_failed"
    );
  };

  const claimDeb = debounce(handleClaim, 1000);

  const transactionInProcess = useMemo(() => {
    return (
      withdrawStatus === "IN_PROGRESS" &&
      withdrawId === index &&
      contractType === transactionContractType
    );
  }, [withdrawStatus, withdrawId]);

  const body = (
    <Box my="20px">
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        border="1px solid"
        px="16px"
        borderColor="brand.primary"
        borderRadius="5px"
      >
        <Flex flexDirection="row" mt="16px">
          <Box mt="2px" mr="16px">
            <Image src={hederaIcon} width="20px" height="20px" />
          </Box>
          <Box>
            {nativeTokenFormatter(Number(amount))} {config.coinName}
          </Box>
        </Flex>
        <Text>
          Transaction fees: ~{nativeTokenFormatter(Number(transactionFee))}{" "}
          {config.coinName}
        </Text>
      </Flex>
      <Box mt="20px">
        <Alert
          type="info"
          message={`HBAR withdrawn will go to your wallet ${selectedAccount}`}
          showMessage={true}
        />
      </Box>
      <Box mt="20px">
        <WalletActionBtn
          text={"Confirm"}
          onClick={() => {
            setConfirmWithdrawModal(false);
            claimDeb();
          }}
          disabled={false}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <Box>
        <Flex mt="32px" mb="12px" fontSize="12px" fontWeight="400">
          <Box flex="1">
            <Text fontSize="14" py="5px">
              {transactionInProcess ? (
                <Spinner size="xs" />
              ) : !amount ? (
                "-"
              ) : (
                <>
                  <Text fontWeight="600" as="span">
                    {nativeTokenFormatter(amount)}{" "}
                  </Text>
                  {NATIVE_TOKEN_LABEL}
                </>
              )}
            </Text>
          </Box>
          <Box flex="1">
            <Text fontSize="14" textAlign="right" py="5px">
              {exchangeRate?.toFixed(precision)}
            </Text>
          </Box>
          <Box flex="2" mr="10px">
            <Box
              fontSize="14px"
              fontWeight="400"
              textAlign="right"
              color="brand.textInactive"
            >
              {isWithDrawn ? (
                <Flex flexDirection="row" justifyContent="flex-end" gap="8px">
                  <Text py="5px">
                    {moment(time).utc().local().format("DD MMM'YY, hh:mm A")}
                  </Text>

                  <Image
                    src={TickIcon}
                    alt="transaction success"
                    height="16px"
                    width="16px"
                  />
                </Flex>
              ) : time > timestamp * 1000 ? (
                <Text py="5px">
                  Available on{" "}
                  {moment(time).utc().local().format("DD MMM'YY, hh:mm A")}
                </Text>
              ) : (
                <Button
                  onClick={() => {
                    setConfirmWithdrawModal(true);
                  }}
                  disabled={transactionInProcess}
                  height="initial"
                  py="6px"
                  bgColor="brand.primary"
                  textColor="brand.textLight"
                  isLoading={transactionInProcess}
                  borderRadius="8px"
                >
                  Withdraw
                </Button>
              )}
            </Box>
          </Box>
        </Flex>
      </Box>

      <ModalWrapper
        onClose={() => setConfirmWithdrawModal(false)}
        modal={confirmWithdrawModal}
        body={body}
        header="Withdraw HBAR"
      />

      <ModalWrapper
        onClose={() => {
          showUnderMaintainanceModal(false);
        }}
        modal={underMaintainanceModal}
        body={<TimerModal />}
        header=""
        styles={{ height: "340px" }}
        showModalCloseButton={false}
        closeOnOverlayClick={false}
      />
    </>
  );
};

export default ClaimItem;
